import React, { useEffect, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';

import type { EmailDeliveryStatusKey } from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';
import {
  useGetEmailDeliveryStatus,
  useUpdateEmailDeliveryStatus,
} from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';

import StatusDialogBody from './StatusDialogBody';
import { getInstructionBody } from './constants';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  brand: string;
}

export default function EmailStatusCard({ open, onClose, userId, brand }: Props) {
  const [selectedKey, setSelectedKey] = useState<EmailDeliveryStatusKey | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Array<boolean>>([]);

  const {
    data: deliveryStatus,
    isLoading,
    isError: isFetchError,
    isRefetching,
    refetch,
  } = useGetEmailDeliveryStatus(userId, brand, open);

  const updateStatusMutation = useUpdateEmailDeliveryStatus();

  const instructionBody = selectedKey && deliveryStatus ? getInstructionBody(selectedKey, deliveryStatus) : null;

  useEffect(() => {
    if (!open || !deliveryStatus || selectedKey) return;

    const firstFlaggedKey =
      (Object.entries(deliveryStatus).find(([, value]) => value.status === true)?.[0] as
        | EmailDeliveryStatusKey
        | undefined) || null;

    if (firstFlaggedKey) {
      setSelectedKey(firstFlaggedKey);
      const body = getInstructionBody(firstFlaggedKey, deliveryStatus);
      setCheckedSteps(body.instructions.map(() => false));
    }
  }, [deliveryStatus, open, selectedKey]);

  const handleSelect = (key: EmailDeliveryStatusKey) => {
    updateStatusMutation.reset();
    setSelectedKey(key);
    if (deliveryStatus) {
      const body = getInstructionBody(key, deliveryStatus);
      setCheckedSteps(body.instructions.map(() => false));
    }
  };

  const handleDismiss = () => {
    setSelectedKey(null);
    setCheckedSteps([]);
  };

  const handleClose = () => {
    updateStatusMutation.reset();
    handleDismiss();
    onClose();
  };

  const handleToggleStep = (index: number) => {
    setCheckedSteps((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  const handleRemove = async () => {
    if (!instructionBody?.type || instructionBody.type === 'unsubscribed') return;
    try {
      await updateStatusMutation.mutateAsync({
        userId,
        brand,
        type: instructionBody.type,
      });
    } catch {
      // displayed inline via mutation.isError
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Email delivery status
        <Tooltip title="Refresh status">
          <IconButton
            size="small"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            sx={{
              animation: isRefetching ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' },
              },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
          Use this tool to check whether the customer can receive emails. Select any flagged status below to view
          details and available actions.
        </Alert>
        <StatusDialogBody
          isLoading={isLoading}
          isRefetching={isRefetching}
          isFetchError={isFetchError}
          deliveryStatus={deliveryStatus}
          selectedKey={selectedKey}
          instructionBody={instructionBody}
          checkedSteps={checkedSteps}
          mutation={updateStatusMutation}
          onSelect={handleSelect}
          onDismiss={handleDismiss}
          onToggleStep={handleToggleStep}
          onRemove={handleRemove}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
